import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Header from "../../components/header/header";
import {
  getActivationAnalyticsFirebaseEventsAction,
} from "../../actions/activationAnalytics";

const lineColors = ["#17c1e8", "#cb0c9f", "#82d616", "#fbcf33", "#ea0606", "#344767"];
const activationFunnelSteps = [
  { stepName: "Landing page view", eventName: "website_landing" },
  { stepName: "Chat page view", eventName: "activation_chat_page_view" },
  { stepName: "Step 1: Business Identity", eventName: "activation_business_identity" },
  { stepName: "Step 2: Shareholders & Directors", eventName: "activation_shareholders_directors" },
  { stepName: "Step 3: Goals & Objectives", eventName: "activation_goals_objectives" },
  { stepName: "Step 4: Business Preview", eventName: "activation_business_preview" },
  { stepName: "Step 5: Choose a Plan", eventName: "activation_choose_plan" },
  { stepName: "Step 6: Plan Details (payment page)", eventName: "activation_payment_page" },
  { stepName: "Payment completed (Activated)", eventName: "payment_completed" },
];
const formatDate = (date) => date.toISOString().slice(0, 10);
const getDefaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 27);
  return formatDate(date);
};
const cleanParams = (params) => {
  return Object.keys(params).reduce((nextParams, key) => {
    if (params[key] !== "") {
      nextParams[key] = params[key];
    }
    return nextParams;
  }, {});
};
const getShortFunnelLabel = (label = "") => {
  return label
    .replace(/^Step\s+\d+:\s*/i, "")
    .replace(/\s*\(payment page\)/i, "")
    .replace(/\s*\(Activated\)/i, "")
    .replace("Payment completed", "Payment")
    .replace("Landing page view", "Landing")
    .replace("Chat page view", "Chat")
    .replace("Business Identity", "Identity")
    .replace("Shareholders & Directors", "Shareholders")
    .replace("Goals & Objectives", "Goals")
    .replace("Business Preview", "Preview")
    .replace("Choose a Plan", "Plan")
    .replace("Plan Details", "Plan details");
};
const getNameList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => item?.eventName || item?.name || item)
      .filter(Boolean);
  }

  return [value];
};
const getAllowedEventName = (row, allowedEventName) => {
  const eventNames = [
    ...getNameList(row.eventNames),
    ...getNameList(row.matchedEvents),
    row.eventName,
  ].filter(Boolean);

  return eventNames.includes(allowedEventName) ? allowedEventName : "";
};
const formatAllowedEventNames = (value, allowedEventName) => {
  return getNameList(value).filter((item) => item === allowedEventName).join(", ") || "-";
};
const getAllowedFunnelEventName = (row) => {
  const stepItem = activationFunnelSteps.find((item) => item.stepName === row.stepName);
  const eventName = stepItem ? getAllowedEventName(row, stepItem.eventName) : "";
  return eventName || "-";
};

export default function ActivationAnalytics() {
  const dispatch = useDispatch();
  const [from, setFrom] = useState(getDefaultFromDate());
  const [to, setTo] = useState(formatDate(new Date()));
  const [groupBy, setGroupBy] = useState("day");
  const [eventName, setEventName] = useState("");
  const [platform, setPlatform] = useState("");
  const [streamId, setStreamId] = useState("");
  const [streamName, setStreamName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { firebaseEvents } = useSelector((state) => state.activationAnalytics);

  useEffect(() => {
    const params = cleanParams({
      from,
      to,
      groupBy,
      eventName,
      platform,
      streamId,
      streamName,
      limit: 1000,
    });

    setLoading(true);
    setErrorMessage("");
    dispatch(getActivationAnalyticsFirebaseEventsAction(params))
      .then((resp) => {
        if (!resp?.status) {
          setErrorMessage(resp?.message || resp?.data || "No Firebase analytics events found for this date range.");
        }
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.data || error?.response?.data?.message || "Unable to load activation analytics.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, from, to, groupBy, eventName, platform, streamId, streamName]);

  const graphData = useMemo(() => {
    const labels = firebaseEvents?.labels || [];
    const series = firebaseEvents?.series || [];

    return labels.map((date, index) => {
      const row = { date };
      series.forEach((item) => {
        row[item.eventName] = item.data[index] || 0;
      });
      return row;
    });
  }, [firebaseEvents]);

  const lines = useMemo(() => firebaseEvents?.series || [], [firebaseEvents]);
  const eventTable = useMemo(() => firebaseEvents?.eventTable || [], [firebaseEvents]);
  const funnel = useMemo(() => {
    const responseFunnel = firebaseEvents?.funnel || [];
    return activationFunnelSteps
      .map((stepItem) => responseFunnel.find((row) => {
        return row.stepName === stepItem.stepName || getAllowedEventName(row, stepItem.eventName);
      }))
      .filter(Boolean);
  }, [firebaseEvents]);
  const funnelGraphData = useMemo(() => {
    const labels = firebaseEvents?.funnelGraph?.labels || [];
    const data = firebaseEvents?.funnelGraph?.data || [];
    return activationFunnelSteps.reduce((nextData, stepItem) => {
      const graphIndex = labels.findIndex((label) => label === stepItem.stepName);
      const funnelItem = funnel.find((item) => item.stepName === stepItem.stepName || getAllowedEventName(item, stepItem.eventName));

      if (graphIndex === -1) {
        return nextData;
      }

      nextData.push({
        stepName: stepItem.stepName,
        shortStepName: getShortFunnelLabel(stepItem.stepName),
        usersReached: data[graphIndex] || 0,
        eventCount: funnelItem?.eventCount || 0,
      });

      return nextData;
    }, []);
  }, [firebaseEvents, funnel]);
  const pageTransitions = useMemo(() => {
    return firebaseEvents?.pageTransitions || firebaseEvents?.pageFlow || firebaseEvents?.pageFlowData || [];
  }, [firebaseEvents]);
  const pageTransitionGraphData = useMemo(() => {
    return pageTransitions.map((item) => ({
      transition: `${item.pagePath || "-"} -> ${item.nextPagePath || "-"}`,
      users: item.users || 0,
      sessions: item.sessions || 0,
    }));
  }, [pageTransitions]);
  const noDataMessage = "No Firebase analytics events found for this date range.";
  const hasFunnelValues = funnel.some((item) => item.usersReached || item.eventCount);
  const noFirebaseEvents = !loading && !errorMessage && !graphData.length && !hasFunnelValues && !eventTable.length;

  return (
    <>
      <Header title="Activation Analytics" />
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-lg-12 col-xl-12 col-md-12 mb-lg-0 mb-4">
            <div className="card">
              <div className="card-header pb-0 px-3">
                <div className="row align-items-center">
                  <div className="col-md-3">
                    <h6 className="font-weight-bolder payment-text mb-0">
                      Activation Analytics
                    </h6>
                  </div>
                  <div className="col-md-9">
                    <div className="row g-2 justify-content-end">
                      <div className="col-md-3 col-sm-6">
                        <input className="form-control" type="date" value={from} required onChange={(e) => setFrom(e.target.value)} />
                      </div>
                      <div className="col-md-3 col-sm-6">
                        <input className="form-control" type="date" value={to} required onChange={(e) => setTo(e.target.value)} />
                      </div>
                      {/* <div className="col-md-2 col-sm-6">
                        <select className="form-control" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                          <option value="day">day</option>
                          <option value="month">month</option>
                          <option value="hour">hour</option>
                        </select>
                      </div> */}
                    </div>
                  </div>
                </div>
                {/* <div className="row g-2 mt-3">
                  <div className="col-md-3 col-sm-6">
                    <input className="form-control" placeholder="Event name" value={eventName} onChange={(e) => setEventName(e.target.value)} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <input className="form-control" placeholder="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <input className="form-control" placeholder="Stream ID" value={streamId} onChange={(e) => setStreamId(e.target.value)} />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <input className="form-control" placeholder="Stream name" value={streamName} onChange={(e) => setStreamName(e.target.value)} />
                  </div>
                </div> */}
              </div>
              <div className="card-body p-3">
                {loading && (
                  <h6 className="mb-4 text-sm table-heading">Loading analytics...</h6>
                )}
                {errorMessage && (
                  <h6 className="mb-4 text-sm table-heading">{errorMessage}</h6>
                )}
                {noFirebaseEvents && (
                  <h6 className="mb-4 text-sm table-heading">
                    {noDataMessage}
                  </h6>
                )}

                <div className="row mb-4">
                  <div className="col-xl-3 col-md-6 mb-3">
                    <div className="border-radius-lg bg-gray-100 p-3 h-100">
                      <p className="text-sm mb-1 text-secondary">Overall Conversion</p>
                      <h5 className="mb-0">{firebaseEvents?.overallConversion || 0}%</h5>
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-6 mb-3">
                    <div className="border-radius-lg bg-gray-100 p-3 h-100">
                      <p className="text-sm mb-1 text-secondary">Biggest Drop-off Step</p>
                      <h6 className="mb-0">{firebaseEvents?.biggestDropOff?.stepName || "-"}</h6>
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-6 mb-3">
                    <div className="border-radius-lg bg-gray-100 p-3 h-100">
                      <p className="text-sm mb-1 text-secondary">Biggest Drop-off</p>
                      <h5 className="mb-0">{firebaseEvents?.biggestDropOff?.dropOffFromPrevious || 0}</h5>
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-6 mb-3">
                    <div className="border-radius-lg bg-gray-100 p-3 h-100">
                      <p className="text-sm mb-1 text-secondary">Drop-off Percent</p>
                      <h5 className="mb-0">{firebaseEvents?.biggestDropOff?.dropOffFromPreviousPercent || 0}%</h5>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="font-weight-bolder payment-text mb-3">Activation Funnel</h6>
                  <div style={{ width: "100%", height: 360 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={funnelGraphData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="shortStepName" interval={0} angle={-15} textAnchor="end" height={70} />
                        <YAxis yAxisId="left" label={{ value: "Count", angle: -90, position: "insideLeft" }} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="usersReached" name="Active users" fill="#2d74b9" />
                        <Bar yAxisId="left" dataKey="eventCount" name="Events" fill="#82d616" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="table-responsive mb-4">
                  <h6 className="font-weight-bolder payment-text mb-3">Activation Funnel</h6>
                  <div className="row mt-3 font-weight-bold text-dark">
                    <div className="col-1">Step #</div>
                    <div className="col-2">Step Name</div>
                    <div className="col-2">Event Names</div>
                    <div className="col-2">Matched Events</div>
                    <div className="col-2">Active users who triggered this event</div>
                    <div className="col-1">Events</div>
                    <div className="col-2">Conversion</div>
                  </div>
                  {funnel && funnel.length > 0 ? (
                    funnel.map((item) => (
                      <div key={item.step} className="row mt-3">
                        <div className="col-1 text-sm">{item.step}</div>
                        <div className="col-2 text-sm">{item.stepName}</div>
                        <div className="col-2 text-sm">{formatAllowedEventNames(item.eventNames, getAllowedFunnelEventName(item))}</div>
                        <div className="col-2 text-sm">{formatAllowedEventNames(item.matchedEvents, getAllowedFunnelEventName(item))}</div>
                        <div className="col-2 text-sm">{item.usersReached}</div>
                        <div className="col-1 text-sm">{item.eventCount}</div>
                        <div className="col-2 text-sm">{item.cumulativeConversionPercent}%</div>
                      </div>
                    ))
                  ) : (
                    <h6 className="mb-0 text-sm table-heading mt-3">{noDataMessage}</h6>
                  )}
                </div>

                {/* <h6 className="font-weight-bolder payment-text mb-3">Firebase Events</h6>
                <div style={{ width: "100%", height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={graphData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {lines.map((item, index) => (
                        <Line
                          key={item.eventName}
                          name={item.eventName}
                          type="monotone"
                          dataKey={item.eventName}
                          stroke={lineColors[index % lineColors.length]}
                          strokeWidth={item.eventName === "Total" ? 3 : 2}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="row mt-4">
                  <div className="col-lg-12 col-xl-12 col-md-12 mb-4">
                    <h6 className="font-weight-bolder payment-text mb-3">Events</h6>
                    <div className="table-responsive">
                      <div className="row mt-3 font-weight-bold text-dark">
                        <div className="col-6">Event Name</div>
                        <div className="col-3">Active Users</div>
                        <div className="col-3">Event Count</div>
                      </div>
                      {eventTable && eventTable.length > 0 ? (
                        eventTable.map((item, index) => (
                          <div key={index} className="row mt-3">
                            <div className="col-6 text-sm">{item.eventName}</div>
                            <div className="col-3 text-sm">{item.activeUsers}</div>
                            <div className="col-3 text-sm">{item.eventCount}</div>
                          </div>
                        ))
                      ) : (
                        <h6 className="mb-0 text-sm table-heading mt-3">{noDataMessage}</h6>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12 col-xl-12 col-md-12 mb-4">
                    <h6 className="font-weight-bolder payment-text mb-3">Page Transitions</h6>
                    {pageTransitions && pageTransitions.length > 0 ? (
                      <>
                        <div style={{ width: "100%", height: 320 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={pageTransitionGraphData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="transition" interval={0} angle={-20} textAnchor="end" height={90} />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="users" name="Users" fill="#17c1e8" />
                              <Bar dataKey="sessions" name="Sessions" fill="#fbcf33" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="table-responsive mt-4">
                          <div className="row mt-3 font-weight-bold text-dark">
                            <div className="col-4">Page</div>
                            <div className="col-4">Next Page</div>
                            <div className="col-2">Users</div>
                            <div className="col-2">Sessions</div>
                          </div>
                          {pageTransitions.map((item, index) => (
                            <div key={index} className="row mt-3">
                              <div className="col-4 text-sm">{item.pagePath}</div>
                              <div className="col-4 text-sm">{item.nextPagePath}</div>
                              <div className="col-2 text-sm">{item.users}</div>
                              <div className="col-2 text-sm">{item.sessions}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm table-heading">Page transition data is not available for this report.</div>
                    )}
                  </div>
                  {firebaseEvents?.rows && firebaseEvents.rows.length > 0 && (
                    <div className="col-lg-12 col-xl-12 col-md-12 mb-4">
                      <h6 className="font-weight-bolder payment-text mb-3">Debug Rows</h6>
                      <div className="table-responsive">
                        <div className="row mt-3 font-weight-bold text-dark">
                          <div className="col-3">Date</div>
                          <div className="col-5">Event Name</div>
                          <div className="col-2">Event Count</div>
                          <div className="col-2">Active Users</div>
                        </div>
                        {firebaseEvents.rows.map((item, index) => (
                          <div key={index} className="row mt-3">
                            <div className="col-3 text-sm">{item.date}</div>
                            <div className="col-5 text-sm">{item.eventName}</div>
                            <div className="col-2 text-sm">{item.eventCount}</div>
                            <div className="col-2 text-sm">{item.activeUsers}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
